'use client';

import React, { useId, useState } from 'react';
import { DEMO_INDIVIDUAL_DISPLAY_NAME } from '@/lib/demo-content';
import { RAINBOW_GRADIENT, rainbowFrameStyle } from './ledger-rainbow';

/** Landing “Proof” certificate mock — matches production ledger.ertzyx.com gold style. */
export default function LandingCertificatePreview() {
  const [hovered, setHovered] = useState(false);

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xl font-black text-slate-950">Proof</h3>
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-200/80">
          Eligible only
        </span>
      </div>

      <div
        className={`mt-4 rounded-xl p-[4px] shadow-sm transition-shadow duration-300 ${hovered ? 'shadow-md shadow-amber-500/15' : ''}`}
        style={rainbowFrameStyle()}
      >
        <div className="rounded-[10px] bg-white px-5 py-7 sm:px-7 sm:py-8">
          <p className="text-center text-2xl font-black tracking-tight text-slate-950 sm:text-[1.65rem]">
            ERTZYX
          </p>
          <p className="mt-1 text-center text-[11px] font-medium text-slate-600">
            This certificate is awarded to
          </p>

          <div
            className={`mx-auto mt-6 max-w-[300px] rounded-md px-4 py-3 text-center text-xl font-black tracking-wide text-slate-950 transition-transform duration-300 sm:text-2xl ${hovered ? 'scale-[1.02]' : ''}`}
            style={{ background: RAINBOW_GRADIENT }}
          >
            GOLD VERIFIED
          </div>

          <p className="mx-auto mt-5 max-w-[280px] text-center text-xs leading-relaxed text-slate-700 sm:text-[13px]">
            This impact was verified, documented, and recorded as significant human achievement.
          </p>

          <div className="mt-8 flex items-end justify-between gap-4">
            <div className="min-w-0 text-left">
              <p
                className="font-serif text-3xl italic leading-none text-slate-800 sm:text-[2rem]"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                Sophie L.
              </p>
              <p className="mt-2 text-sm font-bold text-slate-900">
                {DEMO_INDIVIDUAL_DISPLAY_NAME}
              </p>
              <p className="mt-1 text-[10px] text-slate-500">
                Demo Illustration — fictional record
              </p>
            </div>

            <div className="flex-shrink-0" aria-hidden>
              <GoldSeal />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Gold seal with neat symmetric rainbow ribbon tails (production-style). */
function GoldSeal() {
  const uid = useId().replace(/:/g, '');
  const gold = `landingGoldFill-${uid}`;
  const ribbon = `landingRibbonGrad-${uid}`;
  const shadow = `landingSealShadow-${uid}`;

  return (
    <svg
      width={84}
      height={92}
      viewBox="0 0 84 92"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="drop-shadow-[0_3px_8px_rgba(15,23,42,0.16)]"
    >
      <defs>
        <filter id={shadow} x="-15%" y="-8%" width="130%" height="125%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#0f172a" floodOpacity="0.2" />
        </filter>
        <radialGradient id={gold} cx="36%" cy="30%" r="70%" fx="34%" fy="26%">
          <stop offset="0%" stopColor="#FFFEF8" />
          <stop offset="30%" stopColor="#FDE68A" />
          <stop offset="62%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#B45309" />
        </radialGradient>
        <linearGradient id={ribbon} x1="14" y1="78" x2="70" y2="78" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF6B35" />
          <stop offset="22%" stopColor="#FACC15" />
          <stop offset="44%" stopColor="#4ADE80" />
          <stop offset="66%" stopColor="#22D3EE" />
          <stop offset="88%" stopColor="#A78BFA" />
          <stop offset="100%" stopColor="#E879F9" />
        </linearGradient>
      </defs>

      <g filter={`url(#${shadow})`}>
        {/* Ribbons: symmetric tails meeting under the seal */}
        <path
          d="M 42 64 L 36 64 L 28 86 L 34 80 L 40 86 L 42 64 Z"
          fill={`url(#${ribbon})`}
          stroke="rgba(15,23,42,0.06)"
          strokeWidth="0.5"
        />
        <path
          d="M 42 64 L 48 64 L 56 86 L 50 80 L 44 86 L 42 64 Z"
          fill={`url(#${ribbon})`}
          stroke="rgba(15,23,42,0.06)"
          strokeWidth="0.5"
        />

        {/* Gold disc covers ribbon tops */}
        <circle cx="42" cy="34" r="30" fill={`url(#${gold})`} stroke="#92400E" strokeWidth="1.75" />
        <circle
          cx="42"
          cy="34"
          r="26"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.75"
        />
        <text
          x="42"
          y="38.5"
          textAnchor="middle"
          fill="#0f172a"
          fontSize="10.5"
          fontWeight="800"
          fontFamily="Arial, Helvetica, sans-serif"
          letterSpacing="0.05em"
        >
          ERTZYX
        </text>
      </g>
    </svg>
  );
}
